/**
 * Registry der QR-/UTM-Kampagnen (Bergdorftides, Spec 2026-08-21).
 *
 * Diese Datei ist die einzige Wahrheitsquelle fuer Kurzlink, Landing-URL,
 * Voucher-Code und UTM-Parameter. Sie speist den Kurzlink-Handler, den
 * Cookie-Handler in der Middleware, den Checkout-Server-Helper, das Banner,
 * den QR-Generator und das Voucher-Anlage-Skript.
 *
 * Bewusst KEIN NEXT_PUBLIC_-Env fuer `enabled`: ein NEXT_PUBLIC_-Flag ist in
 * diesem Projekt zur Laufzeit schon einmal ein No-op gewesen (Resume-Payment-Fix).
 * Der Kill-Switch ist deshalb eine hartkodierte Konstante, Rollback = Wert auf
 * false und deployen. Ohne Deploy wirkt `?tides=0` pro Session sofort.
 *
 * Diese Datei ist framework-frei und laeuft in Middleware (Edge), Server
 * Components und im Browser-Bundle.
 */

export type QrCampaignKey = "bergdorftides";

export type QrCampaign = {
	/** Kill-Switch. Bei false: kein UTM-Redirect, kein Cookie, kein Banner, kein Auto-Voucher. */
	enabled: boolean;
	/** Kurzlink-Pfad, ohne Trailing Slash, immer unter /go/. */
	shortPath: string;
	/** Ziel des Kurzlinks, ohne Query. */
	landingPath: string;
	/** Saleor-Voucher-Code. Steht NIE im Cookie, wird immer hier nachgeschlagen. */
	voucherCode: string;
	discountPercent: number;
	utm: { source: string; medium: string; campaign: string; content: string };
};

/**
 * `utm.campaign` MUSS dem Registry-Schluessel entsprechen: der Erkennungsschluessel
 * in der Middleware ist `utm_campaign` selbst, ein zusaetzlicher Parameter existiert
 * nicht. Ein Test haelt diese Invariante fest.
 */
export const QR_CAMPAIGNS: Readonly<Record<QrCampaignKey, QrCampaign>> = {
	bergdorftides: {
		enabled: true,
		shortPath: "/go/tides",
		landingPath: "/en/us",
		voucherCode: "TIDES5",
		discountPercent: 5,
		utm: {
			source: "instagram",
			medium: "social",
			campaign: "bergdorftides",
			content: "beacon-qr",
		},
	},
};

/** Attributions-Cookie. httpOnly:false, damit das Banner es clientseitig lesen kann. */
export const CAMPAIGN_COOKIE_NAME = "bb_campaign";
/** Session-Kill-Switch-Cookie, gesetzt durch `?tides=0`. */
export const CAMPAIGN_OFF_COOKIE_NAME = "bb_campaign_off";
/** Merkt sich, dass der Besucher das Banner weggeklickt hat. */
export const CAMPAIGN_DISMISSED_COOKIE_NAME = "bb_campaign_dismissed";
/** 30 Tage Attributionsfenster. Einzige Ablaufquelle, kein expiresAt im Cookie. */
export const CAMPAIGN_COOKIE_MAX_AGE = 30 * 24 * 60 * 60;
/** Query-Parameter des Session-Kill-Switch: `?tides=0`. */
export const CAMPAIGN_KILL_PARAM = "tides";

function isQrCampaignKey(
	value: unknown,
	registry: Readonly<Record<string, QrCampaign>>,
): value is QrCampaignKey {
	return typeof value === "string" && Object.prototype.hasOwnProperty.call(registry, value);
}

/**
 * Schlaegt eine Kampagne nach. Gibt null zurueck, wenn der Schluessel unbekannt ist
 * ODER die Kampagne abgeschaltet ist. Aufrufer muessen deshalb nur auf null pruefen.
 */
export function getQrCampaign(
	key: string | null | undefined,
	registry: Readonly<Record<string, QrCampaign>> = QR_CAMPAIGNS,
): QrCampaign | null {
	if (!isQrCampaignKey(key, registry)) {
		return null;
	}

	const campaign = registry[key];

	return campaign && campaign.enabled ? campaign : null;
}

/**
 * Matcht einen Pfad gegen die Kurzlinks. Exakter Vergleich, ein einzelner
 * Trailing Slash wird toleriert. `/go/tides/x` und `/go/` matchen nicht.
 *
 * Liefert die Kampagne bewusst AUCH bei `enabled: false` zurueck, anders als
 * `getQrCampaign`. Der Aufrufer entscheidet, was mit einer deaktivierten
 * Kampagne passiert: der Kurzlink-Handler in der Middleware muss trotz
 * Kill-Switch weiterleiten, nur ohne UTM auf den nackten `landingPath`.
 * Wuerde diese Funktion selbst filtern, wuerde jeder gescannte QR-Code beim
 * Abschalten der Kampagne 404en, statt auf die Startseite zu landen.
 */
export function getQrCampaignByShortPath(
	pathname: string,
	registry: Readonly<Record<string, QrCampaign>> = QR_CAMPAIGNS,
): { key: QrCampaignKey; campaign: QrCampaign } | null {
	if (!pathname) {
		return null;
	}

	const normalized = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

	for (const [key, campaign] of Object.entries(registry)) {
		if (campaign.shortPath === normalized && isQrCampaignKey(key, registry)) {
			return { key, campaign };
		}
	}

	return null;
}

/**
 * Landing-URL des Kurzlinks: landingPath plus die vier UTM-Parameter in fester
 * Reihenfolge (source, medium, campaign, content). Die Reihenfolge ist Teil des
 * Vertrags, damit QR-Code, Redirect und PostHog-Auswertung dieselbe URL sehen.
 */
export function buildCampaignLandingUrl(campaign: QrCampaign): string {
	const params = new URLSearchParams();
	params.set("utm_source", campaign.utm.source);
	params.set("utm_medium", campaign.utm.medium);
	params.set("utm_campaign", campaign.utm.campaign);
	params.set("utm_content", campaign.utm.content);

	// URLSearchParams kodiert Leerzeichen als "+", die UTM-Ziel-URL soll aber
	// prozentkodiert sein (PostHog und der QR-Scanner lesen beides, Google
	// Analytics-Konventionen und unsere Tests erwarten %20).
	return `${campaign.landingPath}?${params.toString().replace(/\+/g, "%20")}`;
}

/**
 * Liest AUSSCHLIESSLICH `key` aus dem Cookie und validiert ihn gegen die Registry.
 *
 * Sicherheitsrelevant: das Cookie ist httpOnly:false, jeder Besucher kann es per
 * `document.cookie` beschreiben. Ein mitgeschicktes `code`-Feld (oder jedes andere
 * Feld) wird bewusst ignoriert, sonst koennte sich ein Besucher einen fremden
 * Voucher (`RECOVER-XXXXXX`, Affiliate-Code, `WELCOME10`) vom Server automatisch
 * auf den Checkout legen lassen. Der Code kommt immer aus QR_CAMPAIGNS.
 */
export function parseCampaignCookie(raw: string | null | undefined): { key: QrCampaignKey } | null {
	if (!raw) {
		return null;
	}

	for (const decode of [identity, singleDecode, doubleDecode]) {
		try {
			const parsed = JSON.parse(decode(raw)) as unknown;

			if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
				continue;
			}

			const key = (parsed as Record<string, unknown>).key;

			if (!isQrCampaignKey(key, QR_CAMPAIGNS)) {
				continue;
			}

			return { key };
		} catch {
			// naechste Dekodierung versuchen
		}
	}

	return null;
}

/** Cookie-Nutzlast. Nur der Schluessel, niemals der Voucher-Code. */
export function serializeCampaignCookie(key: QrCampaignKey): string {
	return JSON.stringify({ key });
}

function identity(value: string): string {
	return value;
}

function singleDecode(value: string): string {
	return decodeURIComponent(value);
}

function doubleDecode(value: string): string {
	return decodeURIComponent(decodeURIComponent(value));
}
